import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ScamBlockingInactiveBannerProps {
  seniorName: string;
}

/**
 * Shown when the Android app has reported that the Call Screening role
 * is not currently held (`devices/{seniorId}/status/scamBlockingActive === false`).
 *
 * `undefined` is treated as "device hasn't reported yet" and the banner is
 * NOT shown — this keeps it quiet for pre-rollout devices and accounts
 * whose phone has never been activated.
 *
 * No action button: the caregiver must physically touch the senior's phone
 * to grant the system role. An in-dashboard button can't fix it.
 */
export const ScamBlockingInactiveBanner: FC<ScamBlockingInactiveBannerProps> = ({
  seniorName,
}) => {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      className="flex items-start gap-4 p-4 rounded-2xl border border-amber-200 mb-4"
      style={{
        background:
          'linear-gradient(135deg, rgba(254, 243, 199, 0.9) 0%, rgba(254, 249, 231, 0.9) 100%)',
      }}
    >
      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
        <span className="material-symbols-outlined text-amber-700" style={{ fontSize: '22px' }}>
          shield_lock
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-display font-bold text-amber-900">
          {t('banners.scamBlockingInactive.title')}
        </h3>
        <p className="text-xs text-amber-900/80 mt-0.5 leading-relaxed">
          {t('banners.scamBlockingInactive.body', { name: seniorName })}
        </p>
      </div>
    </div>
  );
};

export default ScamBlockingInactiveBanner;
